/**
 * OrderTicket — port of app/callbacks/orders.render_orders_panel.
 *
 * Sections preserved 1:1:
 *   - BROKER ORDER PANEL: broker select, connect, status, symbol, side,
 *     qty, type, limit, stop, TIF, algo, slices, options leg, place.
 *   - POSITION SIZER: risk $, stop %, calculate.
 *   - MULTI-BROKER FIRE: symbol, side, qty, type, price, connect all,
 *     fire all.
 *
 * Callbacks (estimate_cost, place_order, calc_position_size, multi_fire)
 * are hit via the Cloudflare Router worker rather than a Dash callback.
 */
import { useEffect, useState } from 'react';
import { useStore } from '@/store';
import { api, HttpError } from '@/lib/api';
import { getUnlockedBrokers, isVaultUnlocked, redirectToVault } from '@/lib/vault';
import { destinationsFor, entryForBroker, isBrokerName } from '@/lib/brokerCreds';
import { type BrokerStatus, modeFromStatus } from '@/lib/tradingMode';
import { confirmOrderFor, confirmFanOut } from '@/lib/orderConfirm';
import toast from 'react-hot-toast';
import { ORG, GRN, MUT, FONT } from '@/theme';

// Paper vs live is decided by the ROUTER from the credentials the order
// carries (tradingModeFor), and reported per broker on GET /api/broker/status
// (mode_source: "credentials"). The badge and the confirmation read that, and
// nothing else -- see lib/tradingMode.ts.
//
// This list used to offer "Alpaca (paper)" and "Alpaca (live)" as separate
// choices and then collapse both to 'alpaca' before sending. A user who
// deliberately selected paper could therefore be placing live orders — the
// single most dangerous thing a trading UI can do. The selector is gone;
// the router's mode is fetched from /api/broker/status and displayed as an
// unmissable badge instead of a control that lies.
const BROKERS = [
  { label: 'Alpaca',     value: 'alpaca'    },
  { label: 'IBKR',       value: 'ibkr'      },
  { label: 'Tradovate',  value: 'tradovate' },
];

/** One leg of a multi-account fan-out, as POST /api/orders/multi returns it. */
interface MultiOrderLeg {
  index: number;
  label?: string;
  broker: string;
  status: 'filled' | 'accepted' | 'rejected' | 'skipped';
  clientOrderId?: string;
  error?: string;
}
interface MultiOrderResult {
  ok: boolean;
  requestId: string;
  legs: MultiOrderLeg[];
}

// The vault-entry -> broker mapping and the fan-out destination builder moved
// to lib/brokerCreds.ts, so the request headers (lib/api.ts), the single
// order, the fan-out and the DOM ladder all resolve credentials the SAME way.
// BrokerStatus (GET /api/broker/status) lives in lib/tradingMode.ts.

const ORDER_TYPES = [
  { label: 'Market',     value: 'market' },
  { label: 'Limit',      value: 'limit'  },
  { label: 'Stop',       value: 'stop'   },
  { label: 'Stop-Limit', value: 'stop_limit' },
];

const TIFS = [
  { label: 'Day',  value: 'day' },
  { label: 'GTC',  value: 'gtc' },
  { label: 'IOC',  value: 'ioc' },
  { label: 'FOK',  value: 'fok' },
];

const SIDES = [
  { label: 'BUY',   value: 'buy'   },
  { label: 'SELL',  value: 'sell'  },
  { label: 'SHORT', value: 'short' },
  { label: 'COVER', value: 'cover' },
];

const ALGOS = [
  { label: 'None (single order)', value: 'none' },
  { label: 'TWAP — coming soon',  value: 'twap' },
  { label: 'VWAP — coming soon',  value: 'vwap' },
];

function Lbl({ children }: { children: string }) {
  return (
    <div style={{ color: MUT, fontSize: 9, letterSpacing: 1, fontFamily: FONT, marginBottom: 1 }}>
      {children}
    </div>
  );
}

function Field({ children }: { children: React.ReactNode }) {
  return <div style={{ minWidth: 0, flex: '1 1 0' }}>{children}</div>;
}

const inputStyle: React.CSSProperties = {
  width: '100%', fontSize: 11, height: 24, padding: '2px 6px',
};

// Small inline pill used by the seed-line buttons.
// Border uses color-mix() so it works for both literal hex (#ff3333)
// and CSS-var (var(--accent)) inputs — the hex-with-alpha-suffix trick
// only works on real hex values, and accent is now a CSS var.
function pillBtn(color: string): React.CSSProperties {
  return {
    background: 'transparent',
    color,
    border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`,
    fontFamily: "Consolas, monospace",
    fontSize: 9,
    letterSpacing: 0.5,
    padding: '2px 6px',
    borderRadius: 2,
    cursor: 'pointer',
  };
}

export function OrderTicket() {
  const focus = useStore(s => s.focusSymbol);
  const vaultOk = isVaultUnlocked();

  // Chart-line bracket state (lives in the store so the chart sees it).
  // TP/SL are also passed in the order POST body so the broker can hold
  // the bracket server-side once we wire bracket-order semantics in
  // /api/orders (Alpaca: order_class=bracket + take_profit/stop_loss).
  const entryPx = useStore(s => s.entryPrice);
  const tpPx    = useStore(s => s.tpPrice);
  const slPx    = useStore(s => s.slPrice);
  const setEntry = useStore(s => s.setEntry);
  const setTp    = useStore(s => s.setTp);
  const setSl    = useStore(s => s.setSl);

  // Broker order state
  // 'alpaca_paper' was not a value in BROKERS, so the default render
  // matched no row and the mode badge read UNKNOWN.
  const [broker, setBroker] = useState('alpaca');
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState<BrokerStatus | null>(null);
  const [sym, setSym]     = useState(focus);
  const [side, setSide]   = useState('buy');
  const [qty, setQty]     = useState<number | ''>(100);
  const [type, setType]   = useState('market');
  const [lim, setLim]     = useState<number | ''>('');
  const [stp, setStp]     = useState<number | ''>('');
  const [tif, setTif]     = useState('day');
  const [algo, setAlgo]   = useState('none');
  const [slices, setSlices] = useState<number | ''>(1);

  // Options leg
  const [isOpt, setIsOpt]       = useState(false);
  const [optType, setOptType]   = useState('call');
  const [strike, setStrike]     = useState<number | ''>('');
  const [expiry, setExpiry]     = useState('');

  // Position sizer
  const [riskUsd, setRiskUsd] = useState<number | ''>(500);
  const [stopPct, setStopPct] = useState<number | ''>(2);
  const [psResult, setPsResult] = useState<string>('');

  // Multi-broker fire — symbol/side/qty/type/price + broker flags.
  const [mSym, setMSym] = useState(focus);
  const [mSide, setMSide] = useState('buy');
  const [mQty, setMQty] = useState<number | ''>(100);
  const [mType, setMType] = useState('market');
  const [mPrice, setMPrice] = useState<number | ''>('');
  const [mAlpaca, setMAlpaca] = useState(true);
  const [mIbkr, setMIbkr] = useState(false);
  const [mTradovate, setMTradovate] = useState(false);
  const [mResult, setMResult] = useState<string>('');
  const [mAllConnected, setMAllConnected] = useState(false);
  // In-flight guards. Both PLACE ORDER and FIRE ALL were re-clickable
  // while a submission was in the air, so a double-click AFTER confirming
  // sent two orders.
  const [placing, setPlacing] = useState(false);
  const [firing, setFiring] = useState(false);

  // Estimated cost (mirrors estimate_cost callback)
  const estPrice = type === 'market' || lim === '' ? null : Number(lim);
  // Market orders showed "—" for estimated cost — precisely the case where
  // the trader has no price anchor and most needs one. Fall back to the
  // chart's entry line / last limit as a reference so a size mistake is
  // visible before the confirmation, not after the fill.
  const refForEstimate = estPrice ?? (entryPx ?? (typeof lim === 'number' ? lim : null));
  const estCost = refForEstimate && typeof qty === 'number'
    ? `${type === 'market' ? '≈' : '~'}$${(refForEstimate * qty).toFixed(2)}`
    : '—';
  const estSlip = type === 'market'
    ? (estPrice ? '~0.05% slip' : 'market — fill price not guaranteed')
    : 'limit';

  // /api/broker/status returns HTTP 200 with connected:false when nothing
  // is configured, and ignores ?broker=. The old code treated "the request
  // didn't throw" as "connected", so the pill went green for IBKR and
  // Tradovate on the strength of Alpaca's config — and that green state
  // un-disabled the FIRE ALL BROKERS button. Read the body.
  // Fetch status on MOUNT, not only when the user clicks CONNECT. The
  // live/paper badge and the confirmation dialog both read from it, and
  // requiring a click first meant the default render said "UNKNOWN
  // account" on the exact path every user takes.
  useEffect(() => {
    let alive = true;
    api<BrokerStatus>('/api/broker/status')
      .then(s => {
        if (!alive) return;
        setStatus(s);
        const row = s.brokers?.find(b => b.name === broker);
        setConnected(row ? row.connected : false);
      })
      // An unreadable status is MODE UNKNOWN, not the last answer we had:
      // a stale "PAPER" badge over a now-live vault entry is the failure
      // this badge exists to prevent.
      .catch(() => { if (alive) { setStatus(null); setConnected(false); } });
    return () => { alive = false; };
  }, [broker]);

  const onConnect = async () => {
    try {
      const s = await api<BrokerStatus>('/api/broker/status');
      setStatus(s);
      const row = s.brokers?.find(b => b.name === broker);
      const isUp = row ? row.connected : false;
      setConnected(isUp);
      if (isUp) toast.success(`Connected: ${broker} (${modeFromStatus(s, broker).label})`);
      else toast.error(`${broker} is not connected — unlock its broker vault entry`);
    } catch (e) {
      setConnected(false);
      setStatus(null);
      toast.error(`Connect failed: ${(e as Error).message}`);
    }
  };

  // THE BADGE IS THE ROUTER'S ANSWER, NOT OURS.
  //
  // The previous fix derived the badge from the vault ENTRY NAME ("vault
  // first"), which agreed with the router only as long as two classifiers
  // happened to agree -- and IBKR showed "LIVE" where the router says
  // "unknown" (cannot be proven paper). The router now computes the mode from
  // the same credentials the order carries and reports it per broker on
  // /api/broker/status; api() sends exactly those credentials. One source.
  const modeInfo = modeFromStatus(status, broker);
  const isLive = modeInfo.connected && modeInfo.realMoney;
  const brokerMode = modeInfo.known ? (modeInfo.mode ?? 'unknown') : 'unknown';

  // Every order path goes through lib/orderConfirm.ts: a FRESH status read at
  // the moment of confirmation, refusal when the mode cannot be read, and a
  // typed "LIVE" for real money -- the only thing that sets confirmed_mode.
  const orderSummary = (o: {
    verb: string; qty: number; symbol: string; type: string;
    price?: number | null; extra?: string; multiplier?: number;
  }): string => {
    const px = o.price != null && Number.isFinite(o.price) ? o.price : null;
    const mult = o.multiplier ?? 1;
    const notional = px != null
      ? ` ~ $${(px * o.qty * mult).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
      : ' -- NO PRICE REFERENCE (market order)';
    return `${o.verb.toUpperCase()} ${o.qty} ${o.symbol}\n` +
      `${o.type.toUpperCase()}${px != null ? ` @ ${px}` : ''}${notional}\n` +
      `Broker: ${broker}\n` +
      (o.extra ? `${o.extra}\n` : '');
  };

  const onPlace = async () => {
    if (placing) return;   // in-flight guard: a second click must not send a second order
    // THE CREDENTIAL FOR THE SELECTED BROKER -- not the vault's "active"
    // entry. Sending getUnlockedVault() put whichever entry was unlocked last
    // on the order whatever broker was selected: an IBKR token sent to Alpaca
    // as an Alpaca key. One credential, one broker.
    const sel = isBrokerName(broker) ? entryForBroker(getUnlockedBrokers(), broker) : null;
    if (!sel || !sel.entry) {
      toast.error(sel && sel.problem === 'ambiguous'
        ? `Both ${sel.names.join(' and ')} are unlocked for ${broker} -- lock one so it is clear which account trades. Nothing was sent.`
        : `Unlock your ${broker} vault entry to place orders -- OmniStrat only trades your own account.`);
      return;
    }
    const entry = sel.entry;
    if (typeof qty !== 'number' || qty <= 0) { toast.error('Qty required'); return; }
    if (!Number.isFinite(qty) || qty > 1_000_000) { toast.error('Qty exceeds the per-order ceiling'); return; }
    // Bracket sanity check: BUY should have TP above entry and SL below;
    // SELL inverts. We don't refuse the order — the broker may have its
    // own rules — but we warn so a finger-fumbled bracket is visible.
    if (entryPx != null && tpPx != null && slPx != null) {
      const bad = side === 'buy'
        ? (tpPx <= entryPx || slPx >= entryPx)
        : side === 'sell'
        ? (tpPx >= entryPx || slPx <= entryPx)
        : false;
      if (bad) toast(`Bracket looks inverted for ${side.toUpperCase()}`, { icon: '⚠' });
    }
    // Confirm the price that will ACTUALLY be sent. The POST below uses
    // `lim ?? entryPx`, so confirming `lim` alone meant an empty limit box
    // plus a dragged entry line showed "no price" and then submitted a
    // limit at the chart line.
    const sentPrice = typeof lim === 'number' ? lim : (entryPx ?? null);
    const isOption = isOpt && typeof strike === 'number' && !!expiry;
    const confirmed = await confirmOrderFor(broker, orderSummary({
      verb: side,
      qty,
      symbol: isOption ? `${sym} ${expiry} ${strike}${optType === 'call' ? 'C' : 'P'}` : sym,
      type,
      price: sentPrice,
      // An options contract controls 100 shares. Confirming it as plain
      // equity understated the notional by ~100x.
      multiplier: isOption ? 100 : 1,
      extra: isOption ? 'OPTIONS: 1 contract = 100 shares.' : undefined,
    }));
    if (!confirmed) return;
    if (confirmed.status) setStatus(confirmed.status);
    setPlacing(true);
    try {
      await api('/api/orders', {
        method: 'POST',
        body: JSON.stringify({
          symbol: sym,
          // Options: send the leg as structured fields — the backend
          // builds the proper OCC symbol (ROOT+YYMMDD+C/P+strike*1000).
          option: isOpt && typeof strike === 'number' && expiry
            ? { type: optType as 'call' | 'put', strike, expiry }
            : undefined,
          qty,
          side,
          orderType: type,
          tif,
          limitPrice: typeof lim === 'number' ? lim : (entryPx ?? undefined),
          stopPrice:  typeof stp === 'number' ? stp : undefined,
          // Backend expects alpaca|ibkr|tradovate; paper/live is an env
          // concern (ALPACA_MODE), not an order field.
          broker,
          algo: algo === 'none' ? undefined : algo,
          slices: typeof slices === 'number' ? slices : undefined,
          // Bracket legs — backend wires these to Alpaca's order_class=bracket
          // (take_profit.limit_price + stop_loss.stop_price) when ready.
          takeProfit: tpPx ?? undefined,
          stopLoss:   slPx ?? undefined,
          // ── YOUR ACCOUNT, NOT THE SHARED ONE ────────────────────────
          //
          // Without these the order fills in the PLATFORM's brokerage
          // account, shared by every tenant. The vault is end-to-end
          // encrypted, so the server cannot decrypt it — the key is
          // unwrapped here, in the browser, and sent per request. It is
          // never persisted server-side.
          //
          // Every server-side control (kill switch, symbol halt, risk
          // engine, Reg SHO marking, idempotency, audit chain) runs
          // identically either way. Trading your own account does not
          // mean trading ungoverned.
          broker_api_key:    entry.key,
          broker_api_secret: entry.secret ?? undefined,
          broker_mode:       entry.wireMode,
          // What the trader confirmed. "live" only after a typed LIVE; the
          // router refuses a real-money order without it (409
          // TRADING_MODE_MISMATCH) and refuses a paper echo that turns out
          // to be wrong.
          confirmed_mode:    confirmed.confirmedMode,
        }),
      }) as { account_scope?: string };
      toast.success(
        `Order accepted: ${side.toUpperCase()} ${qty} ${sym}${tpPx || slPx ? ' (bracket)' : ''} -- ${confirmed.info.label} (${entry.label})`,
      );
    } catch (e) {
      const err = e as HttpError;
      // A 409 means the server SUPPRESSED this as a duplicate — no new
      // order exists. Say that, rather than "rejected", which would send
      // the trader looking for a broker problem that isn't there.
      if (err.error === 'TRADING_MODE_MISMATCH') {
        // NOT a generic rejection: the router wants an explicit live
        // confirmation (or the account's mode changed under the dialog).
        // Refresh the badge so the next attempt shows the real mode.
        toast.error(err.message, { duration: 12000 });
        void api<BrokerStatus>('/api/broker/status').then(setStatus).catch(() => setStatus(null));
      } else if (err.error === 'DUPLICATE_ORDER_SUPPRESSED') {
        toast(`Duplicate suppressed — NO new order was placed. The earlier order is the live one.`,
              { icon: '⚠', duration: 7000 });
      } else if (typeof err.status !== 'number') {
        // NO HTTP RESPONSE = UNKNOWN, NOT REJECTED.
        //
        // A transport failure (network drop, timeout, CORS) has no
        // .status, so it fell through to "Order rejected" -- but the
        // request may well have reached the Router and placed a LIVE
        // order. Telling a trader their order was rejected when it might
        // be working is the single most dangerous lie a trading UI can
        // tell, because the reflex is to fire again. The Router is
        // careful about exactly this direction; this handler undid it.
        toast(
          `Could not confirm this order — the connection failed before we heard back. ` +
          `It MAY have been placed. Check the blotter before retrying.`,
          { icon: '⚠', duration: 12000 },
        );
      } else {
        toast.error(`Order not placed: ${err.message}`);
      }
    } finally {
      setPlacing(false);
    }
  };

  // Helper: seed each line at a sensible default if currently null, so the
  // user can drag instead of having to type a number to get a line to appear.
  const lastClose = lim || entryPx || 0;  // best guess available pre-fill
  const seedEntry = () => setEntry(entryPx ?? (typeof lim === 'number' ? lim : null) ?? null);
  const seedTp    = () => {
    const ref = entryPx ?? (typeof lim === 'number' ? lim : 0);
    setTp(side === 'sell' ? +(ref * 0.98).toFixed(2) : +(ref * 1.02).toFixed(2));
  };
  const seedSl    = () => {
    const ref = entryPx ?? (typeof lim === 'number' ? lim : 0);
    setSl(side === 'sell' ? +(ref * 1.01).toFixed(2) : +(ref * 0.99).toFixed(2));
  };
  void lastClose; // referenced for future enhancement (seeding from live quote)

  const onCalcPS = () => {
    if (typeof riskUsd !== 'number' || typeof stopPct !== 'number') {
      setPsResult('Need risk $ and stop %');
      return;
    }
    // Correct sizing: risk-per-share = price × (stop% / 100), so
    // shares = riskUsd / risk-per-share.
    //
    // The previous version omitted price entirely — `riskUsd / (stopPct/100)`
    // — which is dimensionally meaningless and, at the default $500 risk /
    // 2% stop, returned 25,000 shares where the correct answer for a $200
    // stock is 125. Roughly 200× oversized, presented as a helpful
    // suggestion. The displayed notional was `shares * 1`, i.e. also wrong.
    const refPx = (typeof lim === 'number' && lim > 0)
      ? lim
      : (entryPx != null && entryPx > 0 ? entryPx : null);
    if (refPx == null) {
      setPsResult('Need a reference price — set a limit price or drag the entry line');
      return;
    }
    const riskPerShare = refPx * (stopPct / 100);
    if (!(riskPerShare > 0)) { setPsResult('Stop % must be greater than 0'); return; }
    const shares = Math.floor(riskUsd / riskPerShare);
    if (shares <= 0) { setPsResult('Risk budget is smaller than one share of risk'); return; }
    const notional = shares * refPx;
    setPsResult(
      `Risk $${riskUsd} / ${stopPct}% stop @ $${refPx.toFixed(2)} → ${shares.toLocaleString()} shares ` +
      `(notional ≈ $${notional.toLocaleString(undefined, { maximumFractionDigits: 0 })}, ` +
      `risk/share $${riskPerShare.toFixed(2)})`
    );
  };

  // Multi-broker fire handlers. CONNECT ALL probes every selected broker;
  // FIRE ALL submits the same order to each in parallel via /api/orders.
  const selectedBrokers = (() => {
    const xs: string[] = [];
    // Normalized names — the backend dispatch layer routes each POST to
    // the actual broker adapter now (previously all N hit Alpaca).
    if (mAlpaca)    xs.push('alpaca');
    if (mIbkr)      xs.push('ibkr');
    if (mTradovate) xs.push('tradovate');
    return xs;
  })();

  const onConnectAll = async () => {
    setMResult('Probing brokers...');
    // /api/broker/status ignores ?broker= and returns 200 with the full
    // roster. Counting fulfilled promises therefore reported "3/3
    // connected" with ZERO brokers configured — and that state un-disables
    // the FIRE ALL button. One call, read the body.
    try {
      const s = await api<BrokerStatus>('/api/broker/status');
      setStatus(s);
      const live = selectedBrokers.filter(b => s.brokers?.find(x => x.name === b)?.connected);
      const allUp = live.length === selectedBrokers.length && selectedBrokers.length > 0;
      setMAllConnected(allUp);
      const missing = selectedBrokers.filter(b => !live.includes(b));
      setMResult(
        `Connected: ${live.length}/${selectedBrokers.length}` +
        (missing.length ? ` — not configured: ${missing.join(', ')}` : ''),
      );
    } catch (e) {
      setMAllConnected(false);
      setMResult(`Probe failed: ${(e as Error).message}`);
    }
  };

  const onFireAll = async () => {
    if (firing) return;   // in-flight guard
    if (!isVaultUnlocked()) { setMResult('Unlock your broker vault to fire orders.'); toast.error('Unlock your broker vault to trade.'); return; }
    if (typeof mQty !== 'number' || mQty <= 0) { setMResult('Qty required'); return; }
    if (!Number.isFinite(mQty) || mQty > 1_000_000) { setMResult('Qty exceeds the per-order ceiling'); return; }
    if (selectedBrokers.length === 0)          { setMResult('Pick at least one broker'); return; }
    // N simultaneous orders across N venues is the highest-consequence
    // action in this app. Resolve the ACTUAL destinations (one unlocked vault
    // entry per selected broker, lib/brokerCreds.ts) and refuse on a missing
    // or ambiguous one BEFORE asking -- the dialog must describe what will
    // actually fire.
    const dests = destinationsFor(getUnlockedBrokers(), selectedBrokers);
    // Refuse rather than fire what we can. A partial fan-out the trader did
    // not ask for is a position they did not size.
    if (dests.missing.length) {
      const msg = `Not fired — no single unlocked vault entry for: ${dests.missing.join(', ')}. `
        + `Unlock those brokers (or lock the duplicate), or untick them.`;
      setMResult(msg);
      toast.error(msg);
      return;
    }
    // Modes come from the router (fresh /api/broker/status), never from the
    // entry names; any real-money leg needs a typed LIVE.
    const fan = await confirmFanOut(
      dests.destinations.map(d => d.broker),
      `${mSide.toUpperCase()} ${mQty} ${mSym} ${mType.toUpperCase()}` +
      `${typeof mPrice === 'number' ? ` @ ${mPrice}` : ''}\n\n` +
      `FAN-OUT: ${selectedBrokers.length} SEPARATE orders for a TOTAL of ` +
      `${mQty * selectedBrokers.length} ${mSym}.\n` +
      `Funds: YOUR OWN accounts (BYOK vault: ${dests.destinations.map(d => d.label).join(', ')})\n`,
    );
    if (!fan) return;
    setFiring(true);
    setMResult(`Firing on ${selectedBrokers.length} brokers...`);

    // ONE REQUEST TO THE HARDENED PATH, NOT N REQUESTS TO THE SINGLE PATH.
    //
    // This used to loop POST /api/orders once per broker with NO vault
    // credentials attached, which meant every leg fell through to the SHARED
    // platform account. The app's headline feature ran on its least
    // credentialed code path, and Promise.allSettled reported "accepted" for
    // orders placed in an account the trader does not own.
    //
    // POST /api/orders/multi exists for exactly this and does what the loop
    // could not: it refuses the WHOLE request if any destination fails its
    // own risk check, checks each leg against THAT destination's real
    // account, caps the fan-out, and answers 207 rather than 200 when only
    // some legs land.
    // `dests` was resolved and checked BEFORE the confirmation (see above) so
    // the dialog could state the true venues and modes; reuse it.

    // Stable across retries of the SAME intent, so a retry cannot double-fill.
    const requestId = `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

    try {
      const res = await api<MultiOrderResult>('/api/orders/multi', {
        method: 'POST',
        body: JSON.stringify({
          order: {
            symbol: mSym,
            qty: mQty,
            side: mSide,
            orderType: mType,
            tif: 'day',
            limitPrice: typeof mPrice === 'number' ? mPrice : undefined,
          },
          destinations: dests.destinations,
          requestId,
          // "live" only after a typed LIVE when any leg is real money.
          confirmed_mode: fan.confirmedMode,
        }),
      });

      const legs = Array.isArray(res?.legs) ? res.legs : [];
      const placed = legs.filter(l => l.status === 'filled' || l.status === 'accepted');
      const failed = legs.filter(l => l.status === 'rejected');
      const skipped = legs.filter(l => l.status === 'skipped');

      // "Filled" was a lie: a 2xx from the broker is an ACKNOWLEDGEMENT, not
      // a fill. Calling it filled across a fan-out is exactly how a trader
      // talks themselves into re-sizing on top of orders that may still be
      // working — or that may have been rejected downstream.
      setMResult(
        `Accepted ${placed.length} · Rejected ${failed.length}`
        + (skipped.length ? ` · Not sent ${skipped.length}` : '')
        + (failed.length ? ` (${failed.map(l => `${l.broker}: ${l.error ?? 'rejected'}`).join('; ')})` : '')
        + ` — acceptance is not a fill; confirm in the Orders tab.`
      );
      if (placed.length) toast.success(`Multi-fire: ${placed.length} account(s) accepted — verify fills in Orders`);
      if (failed.length || skipped.length) {
        toast.error(`Multi-fire: ${failed.length + skipped.length} leg(s) did not land`);
      }
    } catch (e) {
      // A REFUSAL AND AN UNFINISHED REQUEST ARE DIFFERENT FACTS.
      //
      // The server refuses the whole request before placing anything, so a
      // 4xx here means nothing was sent. A network failure means the opposite
      // is possible: the fan-out may have run and the response was lost.
      const he = e as { status?: number; code?: string; message?: string };
      if (typeof he?.status === 'number' && he.status >= 400 && he.status < 500) {
        // he.message is the router's refusal in words (lib/apiErrors.ts) --
        // for 409 TRADING_MODE_MISMATCH that is "Confirm live trading".
        const msg = `Not fired — ${he.message ?? he.code ?? 'refused'} Nothing was sent to any account.`;
        setMResult(msg); toast.error(msg);
      } else {
        const msg = `The request did not complete, and it is NOT known whether any leg was placed. `
          + `Check the Orders tab before firing again — retrying may double your position.`;
        setMResult(msg); toast.error(msg);
      }
    } finally {
      setFiring(false);
    }
  };

  return (
    <div style={{ fontFamily: FONT, color: '#e0e0e0' }}>
      {/* ── BROKER ORDER PANEL ─────────────────────────────────────── */}
      <div style={{ color: ORG, fontSize: 9, letterSpacing: 2, marginBottom: 6 }}>
        BROKER ORDER PANEL
      </div>

      {/* Row 1: broker + connect + status */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <Field>
          <Lbl>BROKER</Lbl>
          <select value={broker} onChange={(e) => setBroker(e.target.value)} style={inputStyle}>
            {BROKERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <div style={{ alignSelf: 'flex-end' }}>
          <button onClick={onConnect} style={{
            background: '#224422', color: GRN, border: '1px solid #335533',
            fontFamily: FONT, fontSize: 10, padding: '3px 10px',
            borderRadius: 2, height: 24, whiteSpace: 'nowrap',
          }}>CONNECT</button>
        </div>
        <Field>
          <Lbl>STATUS</Lbl>
          <div style={{ fontSize: 10, color: connected ? GRN : MUT, paddingTop: 4 }}>
            {connected ? '● ready' : '○ not connected'}
          </div>
        </Field>
        {/* Paper vs live is server-controlled and used to be invisible —
            and the removed dropdown actively implied the user chose it.
            Show the real mode, loudly when it is live. */}
        <Field>
          <Lbl>MODE</Lbl>
          <div style={{
            fontSize: 10, paddingTop: 4, fontWeight: 'bold',
            color: isLive ? '#fff' : (brokerMode === 'paper' ? GRN : MUT),
            background: isLive ? 'var(--loss)' : 'transparent',
            padding: isLive ? '2px 6px' : '4px 0 0 0',
            borderRadius: isLive ? 2 : 0,
          }}>
            {isLive ? `● ${modeInfo.label === 'LIVE' ? 'LIVE — REAL MONEY' : modeInfo.label}` : `○ ${modeInfo.label}`}
          </div>
        </Field>
      </div>

      {/* Row 2: symbol/side/qty/type/limit/stop */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <Field><Lbl>SYMBOL</Lbl><input value={sym} onChange={(e) => setSym(e.target.value.toUpperCase())} style={inputStyle} /></Field>
        <Field><Lbl>SIDE</Lbl>
          <select value={side} onChange={(e) => setSide(e.target.value)} style={inputStyle}>
            {SIDES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field><Lbl>QTY</Lbl>
          <input type="number" value={qty} onChange={(e) => setQty(e.target.value ? Number(e.target.value) : '')} style={inputStyle} />
        </Field>
        <Field><Lbl>TYPE</Lbl>
          <select value={type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
            {ORDER_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field><Lbl>LIMIT $</Lbl><input type="number" value={lim} onChange={(e) => setLim(e.target.value ? Number(e.target.value) : '')} style={inputStyle} /></Field>
        <Field><Lbl>STOP $</Lbl><input type="number" value={stp} onChange={(e) => setStp(e.target.value ? Number(e.target.value) : '')} style={inputStyle} /></Field>
      </div>

      {/* Row 3: TIF / algo / slices */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <Field><Lbl>TIF</Lbl>
          <select value={tif} onChange={(e) => setTif(e.target.value)} style={inputStyle}>
            {TIFS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        {/* ALGO + SLICES were fully interactive and completely inert: the
            values were sent in the request body and POST /api/orders never
            read them. Selecting "TWAP, 10 slices" to work a large order
            quietly produced ONE full-size order — real slippage, wrong
            assumption, no error. Disabled and labelled until the server
            actually slices, rather than left as a control that lies. */}
        <Field><Lbl>ALGO</Lbl>
          <select
            value={algo}
            disabled
            title="Execution algos are not yet available — orders are sent as a single order."
            onChange={(e) => setAlgo(e.target.value)}
            style={{ ...inputStyle, opacity: 0.45, cursor: 'not-allowed' }}
          >
            {ALGOS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field><Lbl>SLICES</Lbl>
          <input
            type="number"
            value={slices}
            disabled
            title="Execution algos are not yet available — orders are sent as a single order."
            onChange={(e) => setSlices(e.target.value ? Number(e.target.value) : '')}
            style={{ ...inputStyle, opacity: 0.45, cursor: 'not-allowed' }}
          />
        </Field>
      </div>

      {/* Row 3.5: bracket — entry / take-profit / stop-loss with chart sync */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 6, alignItems: 'flex-end',
        paddingTop: 4, borderTop: '1px solid #1a1a1a',
      }}>
        <Field>
          <Lbl>ENTRY $</Lbl>
          <input type="number" step="0.01"
            value={entryPx ?? ''}
            onChange={(e) => setEntry(e.target.value ? Number(e.target.value) : null)}
            style={inputStyle} placeholder="—" />
        </Field>
        <Field>
          <Lbl>TAKE PROFIT $</Lbl>
          <input type="number" step="0.01"
            value={tpPx ?? ''}
            onChange={(e) => setTp(e.target.value ? Number(e.target.value) : null)}
            style={{ ...inputStyle, borderColor: tpPx != null ? GRN : '#2a2a2a' }}
            placeholder="—" />
        </Field>
        <Field>
          <Lbl>STOP LOSS $</Lbl>
          <input type="number" step="0.01"
            value={slPx ?? ''}
            onChange={(e) => setSl(e.target.value ? Number(e.target.value) : null)}
            style={{ ...inputStyle, borderColor: slPx != null ? 'var(--loss)' : '#2a2a2a' }}
            placeholder="—" />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, fontSize: 9, color: MUT }}>
        <button onClick={seedEntry} style={pillBtn(ORG)}>+ ENTRY</button>
        <button onClick={seedTp}    style={pillBtn(GRN)}>+ TP @ {side === 'sell' ? '−2%' : '+2%'}</button>
        <button onClick={seedSl}    style={pillBtn('var(--loss)')}>+ SL @ {side === 'sell' ? '+1%' : '−1%'}</button>
        {(entryPx != null || tpPx != null || slPx != null) && (
          <button onClick={() => { setEntry(null); setTp(null); setSl(null); }} style={pillBtn('#444')}>
            CLEAR LINES
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 9, color: MUT, alignSelf: 'center' }}>
          drag lines on chart ↕
        </span>
      </div>

      {/* Row 4: options leg */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6, alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
          <input type="checkbox" checked={isOpt} onChange={(e) => setIsOpt(e.target.checked)} style={{ accentColor: ORG }} />
          Options
        </label>
        {isOpt && <>
          <Field><Lbl>TYPE</Lbl>
            <select value={optType} onChange={(e) => setOptType(e.target.value)} style={inputStyle}>
              <option value="call">Call</option><option value="put">Put</option>
            </select>
          </Field>
          <Field><Lbl>STRIKE</Lbl><input type="number" value={strike} onChange={(e) => setStrike(e.target.value ? Number(e.target.value) : '')} style={inputStyle} /></Field>
          <Field><Lbl>EXPIRY</Lbl><input placeholder="2026-09-19" value={expiry} onChange={(e) => setExpiry(e.target.value)} style={inputStyle} /></Field>
        </>}
      </div>

      {/* Row 5: cost estimate + place */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 8 }}>
        <div style={{ flex: 1, fontSize: 10, color: vaultOk ? MUT : '#c08a3e' }}>
          {vaultOk
            ? `${estCost} · ${estSlip}`
            : <>Vault locked — <span onClick={redirectToVault} style={{ color: '#d4a45a', cursor: 'pointer', textDecoration: 'underline' }}>unlock</span> to trade your own account</>}
        </div>
        <button onClick={onPlace} disabled={placing || !vaultOk} style={{
          background: 'var(--accent)', color: 'var(--accent-ink)', border: 'none',
          fontFamily: FONT, fontSize: 11, fontWeight: 'bold',
          padding: '6px 18px', borderRadius: 2,
          opacity: (placing || !vaultOk) ? 0.45 : 1, cursor: (placing || !vaultOk) ? 'not-allowed' : 'pointer',
        }}>PLACE ORDER</button>
      </div>

      <hr style={{ border: 0, borderTop: '1px solid #222' }} />

      {/* ── POSITION SIZER ─────────────────────────────────────────── */}
      <div style={{ color: ORG, fontSize: 9, letterSpacing: 2, margin: '8px 0 6px' }}>
        POSITION SIZER
      </div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
        <Field><Lbl>RISK $</Lbl><input type="number" value={riskUsd} onChange={(e) => setRiskUsd(e.target.value ? Number(e.target.value) : '')} style={inputStyle} /></Field>
        <Field><Lbl>STOP %</Lbl><input type="number" value={stopPct} onChange={(e) => setStopPct(e.target.value ? Number(e.target.value) : '')} style={inputStyle} /></Field>
        <button onClick={onCalcPS} style={{
          background: '#1a3355', color: '#88aaff', border: '1px solid #334466',
          fontFamily: FONT, fontSize: 10, padding: '3px 10px',
          borderRadius: 2, height: 24,
        }}>CALCULATE</button>
      </div>
      {psResult && (
        <div style={{ fontSize: 10, color: '#88aaff', marginTop: 4 }}>{psResult}</div>
      )}

      <hr style={{ border: 0, borderTop: '1px solid #222', marginTop: 10 }} />

      {/* ── MULTI-BROKER FIRE ───────────────────────────────────────── */}
      <div style={{ color: ORG, fontSize: 9, letterSpacing: 2, margin: '8px 0 6px' }}>
        MULTI-BROKER FIRE
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <Field><Lbl>SYMBOL</Lbl><input value={mSym} onChange={(e) => setMSym(e.target.value.toUpperCase())} style={inputStyle} /></Field>
        <Field><Lbl>SIDE</Lbl>
          <select value={mSide} onChange={(e) => setMSide(e.target.value)} style={inputStyle}>
            <option value="buy">BUY</option><option value="sell">SELL</option>
          </select>
        </Field>
        <Field><Lbl>QTY</Lbl><input type="number" value={mQty} onChange={(e) => setMQty(e.target.value ? Number(e.target.value) : '')} style={inputStyle} /></Field>
        <Field><Lbl>TYPE</Lbl>
          <select value={mType} onChange={(e) => setMType(e.target.value)} style={inputStyle}>
            {ORDER_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field><Lbl>PRICE $</Lbl><input type="number" value={mPrice} onChange={(e) => setMPrice(e.target.value ? Number(e.target.value) : '')} style={inputStyle} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 6, fontSize: 10 }}>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={mAlpaca}    onChange={(e) => setMAlpaca(e.target.checked)}    style={{ accentColor: ORG }} /> Alpaca
        </label>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={mIbkr}      onChange={(e) => setMIbkr(e.target.checked)}      style={{ accentColor: ORG }} /> IBKR
        </label>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={mTradovate} onChange={(e) => setMTradovate(e.target.checked)} style={{ accentColor: ORG }} /> Tradovate
        </label>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button onClick={onConnectAll} style={{
          background: '#224422', color: GRN, border: '1px solid #335533',
          fontFamily: FONT, fontSize: 10, padding: '4px 10px', borderRadius: 2,
        }}>CONNECT ALL</button>
        <button onClick={onFireAll} disabled={firing || !vaultOk || (!mAllConnected && selectedBrokers.length > 1)} style={{
          background: 'var(--loss)', color: '#fff', border: 'none',
          fontFamily: FONT, fontSize: 11, fontWeight: 'bold',
          padding: '5px 16px', borderRadius: 2,
          opacity: (!vaultOk || (!mAllConnected && selectedBrokers.length > 1)) ? 0.5 : 1,
        }}>FIRE ALL BROKERS</button>
      </div>
      {mResult && (
        <div style={{ fontSize: 10, color: '#bbb', marginTop: 4 }}>{mResult}</div>
      )}
    </div>
  );
}
